import { t } from '@hefe/i18n';
import { Prospect, SelectOption } from '@hefe/models';
import {
  BasketballTeamsSlice,
  ProspectsApi,
  currentUserIsAdminSelector,
} from '@hefe/store';
import {
  CleanHtml,
  formatDate,
  formatDateTime,
  formatTimeAgo,
  fullUserName,
} from '@hefe/utils';
import {
  Col,
  Collapse,
  Descriptions,
  Divider,
  Row,
  Select,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import InlineEditInput from '../../../core/components/inline-edit-input/inline-edit-input.component';
import ProspectReplyForm from '../../forms/prospect-reply/prospect-reply.form';

interface ProspectComponentProps {
  prospect: Prospect;
  teamId?: number;
}

export default function ProspectComponent(props: ProspectComponentProps) {
  const dispatch = useAppDispatch();

  const [prospect, setProspect] = useState(props.prospect);

  const [assignGroups, { isLoading: isAssigningGroups }] =
    ProspectsApi.useAssignGroupsMutation();

  const userIsAdmin = useSelector(currentUserIsAdminSelector);
  const teamsSelect = useSelector(
    BasketballTeamsSlice.basketballTeamsSelectWithoutPendingSelector
  );

  const [loadingTeams, setLoadingTeams] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = () => {
    if (!userIsAdmin) {
      return;
    }

    setLoadingTeams(true);

    dispatch(
      BasketballTeamsSlice.fetchBasketballTeamsSelect({
        cacheDuration: 5 * 60 * 1000,
      })
    )
      .unwrap()
      .finally(() => {
        setLoadingTeams(false);
      });
  };

  const saveGroups = (values: any) => {
    const groupables = values.map((value: number) => {
      return {
        groupable_id: value,
        groupable_type: 'App\\Models\\Basketball\\BasketballTeam',
      };
    });

    assignGroups({
      prospectId: prospect.id,
      request: {
        groupables: groupables,
      },
    })
      .unwrap()
      .then((data) => {
        setProspect(data);
      });
  };

  const renderPrevReplies = () => {
    if (!prospect.replies || prospect.replies?.length === 0) {
      return null;
    }

    const replies = prospect.replies?.map((reply) => {
      return {
        key: reply.id,
        label: t('prospects.replies.previous.text', {
          date: formatDateTime(reply.created_at ?? ''),
        }),
        children: (
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('prospects.status.title')}>
              {t('prospects.replies.previous.replied_by', {
                name: fullUserName(reply.user),
              })}
            </Descriptions.Item>
            <Descriptions.Item label={t('prospects.status.title')}>
              {t('prospects.status.' + reply.status)}
            </Descriptions.Item>
            <Descriptions.Item label={t('team')}>
              {reply.groupable_name}
            </Descriptions.Item>
            {reply.message && (
              <Descriptions.Item label={t('prospects.message')}>
                <div
                  dangerouslySetInnerHTML={{ __html: CleanHtml(reply.message) }}
                />
              </Descriptions.Item>
            )}
          </Descriptions>
        ),
      };
    });

    return (
      <div>
        <Typography.Title level={5}>
          {t('prospects.replies.previous.title')}
        </Typography.Title>
        <Collapse items={replies} />
        <Divider />
      </div>
    );
  };

  return (
    <Row>
      <Col xs={24}>
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('firstname')}>
            {prospect.firstname}
          </Descriptions.Item>
          <Descriptions.Item label={t('lastname')}>
            {prospect.lastname}
          </Descriptions.Item>
          <Descriptions.Item label={t('email')}>
            {prospect.email}
          </Descriptions.Item>
          <Descriptions.Item label={t('phone')}>
            {prospect.phone}
          </Descriptions.Item>
          <Descriptions.Item label={t('birthday')}>
            {formatDate(prospect.birthday)}
          </Descriptions.Item>
          <Descriptions.Item label={t('prospects.experience.title')}>
            {t('prospects.experience.' + prospect.experience)}
          </Descriptions.Item>
          <Descriptions.Item label={t('prospects.message')}>
            {prospect.message}
          </Descriptions.Item>
          <Descriptions.Item label={t('created_at')}>
            {formatDateTime(prospect.created_at ?? '')} - (
            {formatTimeAgo(prospect.created_at ?? '')})
          </Descriptions.Item>
          <Descriptions.Item label={t('team')}>
            <InlineEditInput
              disabled={!userIsAdmin || prospect.status === 'signed'}
              highlightingMode="self"
              component={Select}
              value={prospect.basketball_teams?.map((team) => team.id) ?? []}
              text={
                prospect.basketball_teams
                  ?.map((team) => team.name)
                  .join(', ') ?? t('pending')
              }
              loading={isAssigningGroups}
              onOk={(values) => saveGroups(values)}
              componentProps={{
                options: teamsSelect,
                mode: 'multiple',
                showSearch: true,
                loading: loadingTeams,
                filterOption: (input: string, option: SelectOption) =>
                  option?.label?.toLowerCase().includes(input?.toLowerCase()),
              }}
            />
          </Descriptions.Item>
        </Descriptions>
      </Col>
      <Col xs={24} style={{ padding: 16 }}>
        {renderPrevReplies()}
        {prospect.status !== 'signed' && (
          <>
            <Typography.Paragraph>
              {t('prospects.info.reply.description')}
            </Typography.Paragraph>
            <Typography.Paragraph>
              {t('prospects.info.reply.approved')}
            </Typography.Paragraph>
            <Typography.Paragraph>
              {t('prospects.info.reply.declined')}
            </Typography.Paragraph>
            <Typography.Paragraph>
              {t('prospects.info.reply.waitlist')}
            </Typography.Paragraph>
            <Divider />
            <ProspectReplyForm prospect={prospect} teamId={props.teamId} />
          </>
        )}
      </Col>
    </Row>
  );
}
